import React from 'react'
import { Users, Calendar, BarChart3, Crown, Shield, User, ChevronRight } from 'lucide-react'
import { Team } from '../../types/teams'
import { FILL_GRADIENT } from '../../constants/gradients'

interface TeamListItemProps {
  team: Team
  onInviteMembers: (teamId: string) => void
  onTeamAction: (teamId: string, action: string) => void
}

export const TeamListItem: React.FC<TeamListItemProps> = ({
  team,
  onInviteMembers,
  onTeamAction
}) => {
  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'owner':
        return <Crown className="w-4 h-4 text-yellow-500" />
      case 'admin':
        return <Shield className="w-4 h-4 text-blue-500" />
      default:
        return <User className="w-4 h-4 text-gray-500" />
    }
  }

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'owner':
        return 'bg-gradient-to-r from-yellow-100 to-orange-100 text-orange-700 dark:from-yellow-900/30 dark:to-orange-900/30 dark:text-yellow-300'
      case 'admin':
        return 'bg-gradient-to-r from-blue-100 to-purple-100 text-blue-700 dark:from-blue-900/30 dark:to-purple-900/30 dark:text-blue-300'
      default:
        return 'bg-gray-100 text-gray-700 dark:bg-gray-800/50 dark:text-gray-300'
    }
  }

  const avatarUrl = team.avatarUrl || (team as any).avatar_url

  return (
    <div 
      className="bg-white dark:bg-dark-surface border border-gray-200 dark:border-dark-border rounded-xl p-6 hover:shadow-lg transition-all duration-200 cursor-pointer" 
      onClick={() => onTeamAction(team.id, 'view')}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4 flex-1">
          {/* Team Avatar */}
          <div className="relative">
            {avatarUrl ? (
              <img
                src={avatarUrl}
                alt={team.name}
                className="w-20 h-20 rounded-full object-cover"
              />
            ) : (
              <div 
                className="w-20 h-20 rounded-full flex items-center justify-center"
                style={{ background: 'linear-gradient(135deg, #FCB902 0%, #FF00CF 100%)' }}
              >
                <span className="text-white font-bold text-2xl">
                  {team.name.charAt(0).toUpperCase()}
                </span>
              </div>
            )}
            
            {/* Role Badge */}
            <div className="absolute -bottom-1 -right-1 w-8 h-8 bg-white dark:bg-dark-surface rounded-full flex items-center justify-center border-2 border-white dark:border-dark-surface shadow-sm">
              {getRoleIcon(team.role)}
            </div>
          </div>

          {/* Team Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-dark-text truncate">
                {team.name}
              </h3>
              <span className={`px-2.5 py-1 rounded-md text-xs font-semibold uppercase ${getRoleColor(team.role)}`}>
                {team.role}
              </span>
              {team.isPublic && (
                <span className="px-2.5 py-1 rounded-md text-xs font-semibold uppercase bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300">
                  Public
                </span>
              )}
            </div>
            
            <p className="text-sm text-gray-500 dark:text-dark-text-muted mb-3 line-clamp-2">
              {team.description || 'No description provided'}
            </p>
            
            {/* Team Stats */}
            <div className="flex items-center gap-5 text-sm text-gray-500 dark:text-dark-text-muted">
              <div className="flex items-center gap-1.5">
                <Users className="w-4 h-4" />
                <span className="font-medium">
                  {(
                    (team as any).memberCount ??
                    (team as any).member_count ??
                    ((team as any).members?.length ?? 0)
                  )} members
                </span>
              </div>
              <div className="flex items-center gap-1.5">
                <BarChart3 className="w-4 h-4" />
                <span className="font-medium">
                  {(
                    (team as any).projectCount ??
                    (team as any).game_count ??
                    ((team as any).projects?.length ?? 0)
                  )} games
                </span>
              </div>
              <div className="flex items-center gap-1.5">
                <Calendar className="w-4 h-4" />
                <span className="font-medium">{team.lastActivity ? new Date(team.lastActivity).toLocaleDateString() : 'No activity'}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Action Area */}
        <div className="flex items-center">
          {/* Navigation Arrow */}
          <button
            onClick={(e) => {
              e.stopPropagation()
              onTeamAction(team.id, 'view')
            }}
            className="p-2.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
            title="View team"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  )
} 